import { useEffect, useRef } from "react";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keep keyboard focus inside a custom dialog and restore it on dismissal. */
export const useModalFocus = (onDismiss, busy = false, returnFocusRef = null) => {
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    // An async request may disable the trigger before this dialog mounts,
    // moving browser focus to BODY. Prefer the caller's original trigger.
    previousFocus.current = returnFocusRef?.current?.isConnected
      ? returnFocusRef.current : document.activeElement;
    const first = dialogRef.current?.querySelector(FOCUSABLE);
    (first || dialogRef.current)?.focus();
    return () => {
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
    };
  }, [returnFocusRef]);

  const onKeyDown = event => {
    if (event.key === "Escape") {
      if (!busy) { event.preventDefault(); event.stopPropagation(); dismissRef.current(); }
      return;
    }
    if (event.key !== "Tab") return;
    const buttons = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) || [])]
      .filter(node => node.getClientRects().length > 0);
    if (!buttons.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  };

  return { dialogRef, onKeyDown };
};